"use client";

import { AirQualityComponent } from "@/db/definitions";
import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslations } from "next-intl";
import { AirMeasurement } from "@/lib/types/air";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import AirComponentChart from "../../../_components/AirComponentChart";
import { Skeleton } from "@/components/ui/skeleton";

type Props = {
  locationName: string;
  components: AirQualityComponent[];
  cityId: string;
};

const DAYS_OPTIONS = [7, 14, 30, 60];

const AirCityDetailMeasurments: React.FC<Props> = ({
  components,
  cityId,
  locationName,
}) => {
  const t = useTranslations("air-quality");
  const [selectedComponent, setSelectedComponent] =
    useState<AirQualityComponent>(components[0]);
  const [days, setDays] = useState<number>(DAYS_OPTIONS[0]);

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "resource",
      "air-quality",
      "measurements",
      "city",
      cityId,
      selectedComponent,
      days,
    ],
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      if (!selectedComponent) return null;
      const params = new URLSearchParams({
        component: selectedComponent,
        days: days.toString(),
      });

      const res = await fetch(
        `/api/site/public/resource/air-quality/city/${cityId}/measurements?${params.toString()}`,
      );
      if (!res.ok) {
        throw new Error("Network response was not ok");
      }

      const body = (await res.json()) as {
        success: boolean;
        error?: string;
        data?: AirMeasurement[];
      };

      if (!body.success || !body.data || body.data.length === 0) {
        throw new Error(body.error || "Failed to fetch measurements data");
      }
      return body.data;
    },
  });
  return (
    <Card className="">
      <CardHeader className="">
        <CardTitle className="text-xl">
          {t("city.detail.measurements.title", { name: locationName })}
        </CardTitle>
        <CardDescription>
          {t("city.detail.measurements.description", {
            name: locationName,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 w-full">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center sm:justify-between w-full">
          <div className="flex flex-row gap-2 flex-wrap">
            {components.map((component) => (
              <Button
                key={`${cityId}-${component}`}
                variant={
                  selectedComponent === component ? "default" : "outline"
                }
                onClick={() => setSelectedComponent(component)}
              >
                {t(`components.${component}.short-name`)}
              </Button>
            ))}
          </div>
          <Select
            value={days.toString()}
            onValueChange={(value) => setDays(Number(value))}
          >
            <SelectTrigger aria-label={t("station.detail.measurements.select-days-label")} className="w-fit lg:w-[200px] font-medium text-sm self-end sm:self-center">
              <SelectValue
                placeholder={t("station.detail.measurements.select-days-label")}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {DAYS_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {t(`station.detail.measurements.last-days`, {
                      days: option,
                    })}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        {data ? (
          <div className="relative h-full">
            <AirComponentChart
              component={selectedComponent}
              data={data}
              containerClassName="h-60 md:h-80"
            />
          </div>
        ) : isLoading ? (
          <Skeleton className="h-60 md:h-80 rounded-md" />
        ) : isError ? (
          <div className="flex items-center justify-center italic text-muted-foreground">
            {t("city.detail.measurements.no-data", { name: locationName })}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AirCityDetailMeasurments;
